import { Injectable } from "@angular/core"
import { Observable } from "rxjs"
import { map, switchMap } from "rxjs/operators"
import { ApiService } from "./api.service"
import { environment } from "../../environments/environment"

export interface PresignedUrlResponse {
  upload_url: string
  tmp_key: string
  cloudfront_preview_url: string
  content_type: string
}

export interface UploadResult {
  tmp_key: string
  cloudfront_preview_url: string
}

/** Abort stalled S3 PUTs so the UI can surface a clear failure. */
const S3_UPLOAD_TIMEOUT_MS = 120_000

@Injectable({
  providedIn: "root",
})
export class UploadService {
  private apiUrl = `${environment.apiUrl}/generate-url`

  constructor(private apiService: ApiService) {}

  getPresignedUrl(
    entity: "owners" | "stations" | "recordings" | "production_requests",
    extension: string,
    extraData: Record<string, unknown> = {},
  ): Observable<PresignedUrlResponse> {
    const ext = extension.replace(".", "").toLowerCase()

    return this.apiService.authenticatedPost<PresignedUrlResponse>(this.apiUrl, {
      entity,
      file_extension: ext,
      ...extraData,
    })
  }

  /**
   * Upload via fetch so TokenInterceptor never touches S3 PUTs (avoids status-0 logout)
   * and Content-Type exactly matches the signed URL.
   */
  uploadFileToS3(url: string, file: File, contentType: string): Observable<void> {
    return new Observable<void>((subscriber) => {
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), S3_UPLOAD_TIMEOUT_MS)

      fetch(url, {
        method: "PUT",
        body: file,
        headers: {
          "Content-Type": contentType,
        },
        signal: controller.signal,
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`S3 upload failed with status ${response.status}`)
          }
          subscriber.next()
          subscriber.complete()
        })
        .catch((err: unknown) => {
          const aborted =
            (typeof DOMException !== "undefined" && err instanceof DOMException && err.name === "AbortError") ||
            (err instanceof Error && err.name === "AbortError")
          subscriber.error(
            aborted
              ? new Error("Upload timed out. Please check your connection and try again.")
              : err,
          )
        })
        .finally(() => clearTimeout(timeoutId))

      return () => {
        clearTimeout(timeoutId)
        controller.abort()
      }
    })
  }

  uploadFile(
    file: File,
    entity: "owners" | "stations" | "recordings" | "production_requests",
    extraData: Record<string, unknown> = {},
  ): Observable<UploadResult> {
    const extension = file.name.split(".").pop() || ""

    return this.getPresignedUrl(entity, extension, extraData).pipe(
      switchMap((response) =>
        this.uploadFileToS3(response.upload_url, file, response.content_type).pipe(
          map(() => ({
            tmp_key: response.tmp_key,
            cloudfront_preview_url: response.cloudfront_preview_url,
          })),
        ),
      ),
    )
  }
}
